import loggingApis from '../api/services/logging.service';

export interface RuntimeErrorPayload {
  type: string;
  message: string;
  source: string;
  line: number;
  column: number;
  time: string;
  stack?: string;
  componentName?: string;
  componentPath?: string | null;
  componentStack?: string;
}

export interface PromiseRejectionPayload {
  type: string;
  message: string;
  reason?: any;
  time: string;
  stack?: string;
}

export interface UserInteractionPayload {
  message: string;
  config?: Record<string, any>;
  time?: string;
}
export interface SessionApiLogPayload {
  message: string;
  config?: Record<string, any>;
  time?: string;
  trace?: string;
}

const isLocalEnv = import.meta.env.MODE === 'development';

function extractComponentPath(stack?: string): string | null {
  if (!stack) return null;
  const lines = stack.split('\n');
  const match = lines.find(
    line => line.includes('src/') || line.includes('.tsx') || line.includes('.js')
  );
  return match?.trim() || null;
}

/**
 * Logs runtime errors.
 */
export async function logRuntimeError(error: RuntimeErrorPayload): Promise<void> {
  console.error('[Runtime Error]', error);
  if (!isLocalEnv) {
    const componentPath = error.componentPath ?? extractComponentPath(error.stack);
    await loggingApis.saveLog({
      type: error.type,
      message: error.message,
      config: JSON.stringify({
        source: error.source,
        line: error.line,
        column: error.column,
        time: error.time,
        componentName: error.componentName,
        componentPath,
        componentStack: error.componentStack,
      }),
      trace: error.stack || '',
    });
  }
}

/**
 * Logs unhandled promise rejections.
 */
export async function logPromiseRejectionError(error: PromiseRejectionPayload): Promise<void> {
  console.error('[Unhandled Promise Rejection]', error);
  if (!isLocalEnv) {
    await loggingApis.saveLog({
      type: error.type,
      message: error.message,
      config: JSON.stringify({
        reason: error.reason,
        time: error.time,
      }),
      trace: error.stack || '',
    });
  }
}

/**
 * Logs user interactions (extend as needed).
 */
export async function logUserInteraction(payload: UserInteractionPayload): Promise<void> {
  console.info('[User Interaction]', payload);
  if (!isLocalEnv) {
    await loggingApis.saveLog({
      type: '[User Interaction]',
      message: payload.message,
      config: JSON.stringify(payload.config),
      trace: '',
    });
  }
  // Add API call here if you want to persist user interaction logs
}
export async function logSessionApiActivity(payload: SessionApiLogPayload): Promise<void> {
  console.info('[Session API Activity]', payload);

  if (!isLocalEnv) {
    await loggingApis.saveLog({
      type: '[Session API]',
      message: payload.message,
      config: JSON.stringify(payload.config),
      trace: payload.trace || '',
    });
  }
}
